import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, FileText, Loader2 } from 'lucide-react';
import { REPORT_FORMATS } from '../middleware/services/ReportFileService';
import './ReportDownloadButton.css';

/**
 * The "Download Report" button for single runs and batch runs alike.
 *
 * With one format on offer it is a plain button. When the run also has a PDF,
 * the same button opens a small menu to pick HTML or PDF. It keeps whatever
 * look the host passes in `className`, so each screen's button stays its own.
 */
const ReportDownloadButton = ({ formats, onDownload, downloading, className, label, icon }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const choices = formats.filter((f) => REPORT_FORMATS[f]);
  const hasChoice = choices.length > 1;

  // Close on an outside click or Escape
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (format) => {
    setOpen(false);
    onDownload(format);
  };

  return (
    <span className="report-dl" ref={wrapRef}>
      <button
        type="button"
        className={className}
        onClick={() => (hasChoice ? setOpen((o) => !o) : pick(choices[0] || 'html'))}
        disabled={downloading}
        aria-haspopup={hasChoice ? 'menu' : undefined}
        aria-expanded={hasChoice ? open : undefined}
      >
        {downloading ? <Loader2 size={14} className="report-dl__spin" /> : icon}
        <span>{downloading ? 'Downloading…' : label}</span>
        {hasChoice && !downloading && <ChevronDown size={14} className={`report-dl__chevron${open ? ' report-dl__chevron--open' : ''}`} />}
      </button>

      {hasChoice && open && (
        <span className="report-dl__menu" role="menu" aria-label="Report format">
          {choices.map((format) => (
            <button
              key={format}
              type="button"
              role="menuitem"
              className="report-dl__item"
              onClick={() => pick(format)}
            >
              <FileText size={14} />
              <span>{REPORT_FORMATS[format].label}</span>
              <span className="report-dl__ext">.{REPORT_FORMATS[format].extension}</span>
            </button>
          ))}
        </span>
      )}
    </span>
  );
};

ReportDownloadButton.propTypes = {
  /** Formats this run has, e.g. ['html'] or ['html', 'pdf']. */
  formats: PropTypes.arrayOf(PropTypes.oneOf(Object.keys(REPORT_FORMATS))),
  /** Called with the chosen format. */
  onDownload: PropTypes.func.isRequired,
  downloading: PropTypes.bool,
  /** The host screen's own button class, so the button keeps its look. */
  className: PropTypes.string,
  label: PropTypes.string,
  icon: PropTypes.node,
};

ReportDownloadButton.defaultProps = {
  formats: ['html'],
  downloading: false,
  className: '',
  label: 'Download Report',
  icon: <FileText size={14} />,
};

export default ReportDownloadButton;
